import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Select } from '@ngxs/store';
import { isEqual, isNil } from 'lodash';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, takeUntil, tap } from 'rxjs/operators';
import { QuestionSubmission } from 'src/app/quiz/models/question-submission.model';
import { QuizLabelOption } from 'src/app/quiz/models/quiz-question.model';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { removeDuplicates } from 'src/app/shared/list-utils';

@Component({
  selector: 'app-view-quiz-label-anatomy',
  templateUrl: './view-quiz-label-anatomy.component.html',
  styleUrls: ['./view-quiz-label-anatomy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewQuizLabelAnatomyComponent implements OnInit, OnDestroy {

  @Select(QuizState.getActiveSubmission)
  activeSubmission$: Observable<QuestionSubmission>;
  
  @Input()
  public get options(): QuizLabelOption[] {
    return this._options;
  }
  public set options(options: QuizLabelOption[]) {
    if (!isEqual(options, this._options)) {
      this._options = options;
      this.buildFormOptions(options);
    }
  }

  @Output()
  valueChange = new EventEmitter<string[]>();

  form: FormGroup;
  optionsArray: FormArray;
  private ngUnsubscribe = new Subject();
  private _options: QuizLabelOption[] = [];

  constructor(private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      options: this.formBuilder.array([])
    });
    this.optionsArray = this.form.get('options') as FormArray;
  }

  ngOnInit(): void {
    this.initFormListeners();
    this.init();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initFormListeners() {
    this.form.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(({ options }) => {
        this.valueChange.next(options);
      });
  }

  init() {
    this.activeSubmission$
    .pipe(
      filter((activeSubmission) => !isNil(activeSubmission) && !isNil(activeSubmission.answer)),
      pairwise(),
      takeUntil(this.ngUnsubscribe)
    )
    .subscribe(([lastValues, currentValues]) => {
      const newValues = removeDuplicates(lastValues.answer || [], currentValues.answer || [], '');
      if (!isEqual(currentValues, newValues)) {
        this.form.patchValue({
          options: newValues || []
        });
      }
    });

    this.activeSubmission$
      .pipe(
        filter((activeSubmission) => !isNil(activeSubmission) && !isNil(activeSubmission.answer) && Array.isArray(activeSubmission.answer)),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((activeSubmission) => {
        const formAnswers = this.form.value.options;
        const submittedAnswers = activeSubmission.answer;
        if (!isEqual(formAnswers, submittedAnswers)) {
          this.form.patchValue({
            options: submittedAnswers
          }, { emitEvent: false, onlySelf: true });
        }
      });
  }

  private buildFormOptions(options: QuizLabelOption[]) {
    this.optionsArray.clear();
    options.forEach(() => {
      this.optionsArray.push(this.formBuilder.control(''));
    });
    // set initial value: empty
    this.optionsArray.setValue(
      options.map(() => '')
    );
  }
}
